import React from 'react';
import {
  DynamicFeedOutlined as FeedIcon,
  PeopleOutline as DirectoryIcon,
  ChatBubbleOutline as MessagingIcon,
  ArticleOutlined as NewsletterIcon,
  ConfirmationNumberOutlined as EventsIcon,
  PollOutlined as SurveyIcon,
  StorefrontOutlined as MarketplaceIcon,
  VolunteerActivism as FundraisingIcon,
  HowToVoteOutlined as VotingIcon,
  PaymentsOutlined as DuesIcon,
  VerifiedUserOutlined as VerifyIcon,
  KeyOutlined as SsoIcon,
  CampaignOutlined as AnnounceIcon,
  ContactlessOutlined as TapIcon,
  Diversity3Outlined as GroupsIcon,
  AccountTreeOutlined as HierarchyIcon,
  FolderOutlined as DocsIcon,
  AdminPanelSettingsOutlined as AdminIcon,
  UploadFileOutlined as ImportIcon,
} from '@mui/icons-material';
import { BRAND, BottomCta, usePageMeta } from './shared';

const GROUPS = [
  {
    h: 'Connect & communicate',
    d: 'Keep every member in the loop and every conversation in one place.',
    items: [
      { Icon: FeedIcon, t: 'Home feed', d: 'A personalized hub that surfaces what matters—updates, announcements, events, and activity tailored to each member.' },
      { Icon: DirectoryIcon, t: 'Member directory', d: 'A searchable directory that connects members across chapters, regions, and generations, with privacy controls built in.' },
      { Icon: MessagingIcon, t: 'Chat with @mentions', d: 'Direct messages and group chats for committees, chapters, and friends. @mention someone and they get notified.' },
      { Icon: AnnounceIcon, t: 'Announcements', d: 'Post to one chapter, a region, or the whole organization. Posts aimed beyond your level go to an admin for approval.' },
      { Icon: NewsletterIcon, t: 'Newsletters', d: 'Compose and send beautiful newsletters that reach the right audience and read great on every device.' },
    ],
  },
  {
    h: 'Organize & engage',
    d: 'Plan events, gather input, and rally your community around your mission.',
    items: [
      { Icon: EventsIcon, t: 'Events & ticketing', d: 'Create events, sell tickets, manage RSVPs, and check members in, from the chapter meeting to the national convention.' },
      { Icon: TapIcon, t: 'Tap to pay at the door', d: 'Sell tickets at the door with an iPhone. No card reader to buy or carry.' },
      { Icon: GroupsIcon, t: 'Groups', d: 'Committees and interest groups with their own members, chat, and events.' },
      { Icon: SurveyIcon, t: 'Surveys & polls', d: 'Gather feedback and make data-informed decisions with quick polls and full surveys.' },
      { Icon: VotingIcon, t: 'Voting & elections', d: 'Secret-ballot and ranked-choice elections at any level, with eligibility and tallying handled for you.' },
      { Icon: FundraisingIcon, t: 'Fundraising', d: 'Launch campaigns and product fundraisers to raise funds and rally support behind your mission.' },
      { Icon: MarketplaceIcon, t: 'Marketplace', d: 'Let members buy, sell, and promote within a trusted community space.' },
      { Icon: DuesIcon, t: 'Dues', d: 'Collect dues online and record payments made by Zelle, Venmo, or cash, so treasurers see who has paid.' },
    ],
  },
  {
    h: 'Run your organization',
    d: 'The admin tools behind every level, plus trust and access.',
    items: [
      { Icon: HierarchyIcon, t: 'Multi-level structure', d: 'Headquarters, regions, and chapters, each with its own admins, roster, and dues.' },
      { Icon: DocsIcon, t: 'Document library', d: 'Bylaws, minutes, and forms stored where members can find them.' },
      { Icon: AdminIcon, t: 'Admin console', d: 'Manage members, roles, approvals, and reports from one place.' },
      { Icon: ImportIcon, t: 'Member import & setup wizard', d: 'Import your roster from a CSV and follow a guided setup to go live fast.' },
      { Icon: VerifyIcon, t: 'Verification & profile claiming', d: 'Confirm real members, claim existing profiles, and build trust across your entire network.' },
      { Icon: SsoIcon, t: 'Single sign-on (SSO)', d: 'Give members secure, one-click access using your organization’s existing identity provider.' },
    ],
  },
];

const Features: React.FC = () => {
  usePageMeta('Features');
  return (
    <>
      <section className="page-head">
        <div className="container">
          <h1>Everything you need, in one place.</h1>
          <p>{BRAND} brings your members, chapters, and leadership together in one app, so you can stop juggling group texts, payment apps, and shared drives.</p>
        </div>
      </section>

      <section className="page-body">
        <div className="container">
          {GROUPS.map((g) => (
            <div className="group" key={g.h}>
              <div className="group-head">
                <h2>{g.h}</h2>
                <p className="muted">{g.d}</p>
              </div>
              <div className="grid-3">
                {g.items.map(({ Icon, t, d }) => (
                  <div className="feature-lg" key={t}>
                    <div className="gold-coin"><Icon style={{ fontSize: 24 }} aria-hidden="true" /></div>
                    <h3>{t}</h3>
                    <p>{d}</p>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      </section>

      <BottomCta title="See every feature in action." />
    </>
  );
};

export default Features;
